"use client";

import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";

interface HeroSlideCTA {
  label: string;
  href: string;
  target?: "_self" | "_blank";
}

interface HeroSlide {
  title: string;
  subtitle?: string;
  description: string;
  image: any;
  cta?: string | HeroSlideCTA;
}

interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  slides?: HeroSlide[];
  showCarousel?: boolean;
  backgroundImage?: string;
  badge?: string;
}

function normalizeIndex(index: number, length: number) {
  return ((index % length) + length) % length;
}

export function HeroSection({
  title,
  subtitle,
  description,
  slides,
  showCarousel = false,
  backgroundImage,
}: HeroSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const resumeTimeoutRef = useRef<number | null>(null);

  const validSlides = useMemo(
    () => (slides && slides.length > 0 ? slides : []),
    [slides],
  );

  // Auto-slide every 5 seconds with pause on user interaction
  useEffect(() => {
    if (!showCarousel || validSlides.length <= 1 || !isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => normalizeIndex(prev + 1, validSlides.length));
    }, 5000);
    return () => clearInterval(interval);
  }, [showCarousel, validSlides.length, isAutoPlay]);

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        window.clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      if (validSlides.length === 0) return;
      setCurrentSlide(normalizeIndex(index, validSlides.length));
      setIsAutoPlay(false);

      if (resumeTimeoutRef.current) {
        window.clearTimeout(resumeTimeoutRef.current);
      }

      resumeTimeoutRef.current = window.setTimeout(() => {
        setIsAutoPlay(true);
        resumeTimeoutRef.current = null;
      }, 8000);
    },
    [validSlides.length],
  );

  const goToPrevSlide = useCallback(() => {
    goToSlide(currentSlide - 1 + validSlides.length);
  }, [currentSlide, validSlides.length, goToSlide]);

  const goToNextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  if (showCarousel && validSlides.length > 0) {
    const slide = validSlides[currentSlide];

    return (
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-blue-500 to-blue-400 px-4 py-0 sm:px-6 lg:px-8">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center min-h-[600px] py-8 lg:py-16">
            {/* Left Content */}
            <div className="text-white space-y-6 order-2 lg:order-1">
              <div className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-2 backdrop-blur-sm hover:bg-white/15 transition-colors">
                <span className="text-xs font-semibold uppercase tracking-widest">
                  {slide.subtitle}
                </span>
              </div>

              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight text-balance">
                  {slide.title}
                </h1>
              </div>

              <p className="text-base sm:text-lg text-white/90 leading-relaxed max-h-lg min-h-[90px] overflow-hidden">
                {slide.description}
              </p>


                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  {slide.cta &&
                    (typeof slide.cta === "string" ? (
                      <Button
                        size="lg"
                        className="bg-white text-primary hover:bg-white/95 font-semibold shadow-lg hover:shadow-xl transition-all"
                      >
                        {slide.cta}
                      </Button>
                    ) : (
                      <Button
                        asChild
                        size="lg"
                        className="bg-white text-primary hover:bg-white/95 font-semibold shadow-lg hover:shadow-xl transition-all"
                      >
                        <Link
                          href={slide.cta.href}
                          target={slide.cta.target ?? "_self"}
                        >
                          {slide.cta.label}
                        </Link>
                      </Button>
                    ))}

                    <Button
                      asChild
                      size="lg"
                      className="border-white/60 text-white hover:bg-white/20 backdrop-blur-sm font-semibold transition-all"
                    >
                      <Link href="#lien-he">Liên Hệ Tư vấn</Link>
                    </Button>
                </div>


              {/* Carousel Dots */}
              {validSlides.length > 1 && (
                <div className="flex gap-2 pt-6">
                  {validSlides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToSlide(idx)}
                      aria-label={`Go to slide ${idx + 1}`}
                      className={`transition-all duration-300 rounded-full ${
                        idx === currentSlide
                          ? "bg-white h-3 w-8"
                          : "bg-white/50 hover:bg-white/70 h-2 w-2"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right Image */}
            <div className="relative h-[300px] sm:h-[400px] lg:h-[500px] order-1 lg:order-2">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20 rounded-2xl lg:rounded-3xl z-20" />
              <div className="absolute inset-0 rounded-2xl lg:rounded-3xl overflow-hidden border border-white/20 shadow-2xl">
                <img
                  src={slide.image?.origin}
                  alt={slide.title}
                  className="w-full h-full object-cover transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Carousel Controls */}
          {validSlides.length > 1 && (
            <div className="absolute bottom-6 lg:bottom-8 flex gap-3 z-20">
              <button
                onClick={goToPrevSlide}
                aria-label="Previous slide"
                className="p-2.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all backdrop-blur-sm border border-white/30 hover:border-white/50"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <button
                onClick={goToNextSlide}
                aria-label="Next slide"
                className="p-2.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all backdrop-blur-sm border border-white/30 hover:border-white/50"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Simple hero section (for other pages)
  return (
    <section
      className="relative overflow-hidden px-4 py-12 sm:px-6 sm:py-20 lg:px-8 bg-gradient-to-br from-primary to-blue-500"
      style={
        backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}
      }
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl" />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" />

      <div className="relative mx-auto max-w-4xl text-center space-y-6">
        {/* Subtitle/Badge */}
        {subtitle && (
          <p className="text-blue-100 text-xs sm:text-sm font-semibold uppercase tracking-widest">
            {subtitle}
          </p>
        )}

        {/* Title */}
        {title && (
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-balance leading-tight">
            {title}
          </h1>
        )}

        {/* Description */}
        {description && (
          <p className="text-sm sm:text-base text-blue-50 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}
